import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { toJpeg } from "html-to-image";
import { MessageActions } from "./MessageActions";
import { copyToClipboard } from "../utils";
import DynamicChart from "../ui/DynamicChartRenderer";
import TableRenderer from "./TableRenderer";
import { X, Play, Music, Video, FileText, Download, ExternalLink, ZoomIn, ZoomOut, RotateCw, ChevronLeft , ChevronRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

function PdfModal({ url, onClose, pages }) {
  const filename = url.split("/").pop().split("?")[0] || "document.pdf";
  const [currentPage, setCurrentPage] = useState(pages?.[0] || 1);
  const handlePrev = () => {
    console.log("pages prev", pages, "currentPage", currentPage);
    const currentIndex = pages.indexOf(currentPage);
    if (currentIndex > 0) {
      setCurrentPage(pages[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    console.log("pages next", pages, "currentPage", currentPage);
    const currentIndex = pages.indexOf(currentPage);
    if (currentIndex < pages.length - 1) {
      setCurrentPage(pages[currentIndex + 1]);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal card */}
      <div
        className="relative flex flex-col w-full max-w-4xl h-[88vh] bg-white dark:bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3.5 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700/60 flex-shrink-0">
          {/* Icon */}
          <div className="w-9 h-9 rounded-xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <FileText size={16} className="text-red-500" />
          </div>

          {/* Filename */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">{filename}</p>
            <p className="text-[10px] text-slate-400 dark:text-slate-500">PDF Document</p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-[#1d57a5] dark:hover:text-[#4a8ce3] hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Open in new tab"
            >
              <ExternalLink size={13} /> Open
            </a>
            <div className="flex items-center gap-2">
              {/* Prev */}
              <button
                onClick={handlePrev}
                disabled={currentPage === pages[0]}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={18} />
              </button>

              {/* Current Page Only */}
              <div className="min-w-[40px] h-[30px] flex items-center justify-center rounded-full bg-[#1d57a5] text-white text-sm font-semibold">
                {currentPage}
              </div>

              {/* Next */}
              <button
                onClick={handleNext}
                disabled={currentPage === pages[pages.length - 1]}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={18} />
              </button>

            </div>
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* PDF viewer */}
        <div className="flex-1 overflow-hidden bg-slate-100 dark:bg-slate-800">
          <iframe
            key={currentPage} // 🔥 forces reload when page changes
            src={`${url}#page=${currentPage}&toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
            className="w-full h-full border-0"
            title="PDF Viewer"
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

function MediaPlayerModal({ url, onClose, start, end }) {
  const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(url);
  const mediaRef = useRef(null);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const seekAndPlay = () => {
      if (start != null) media.currentTime = Number(start);
      media.play();
    };

    const handleTimeUpdate = () => {
      if (end != null && media.currentTime >= Number(end)) {
        media.pause();
      }
    };

    // If metadata already loaded, seek immediately; otherwise wait
    if (media.readyState >= 1) {
      seekAndPlay();
    } else {
      media.addEventListener("loadedmetadata", seekAndPlay, { once: true });
    }

    media.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      media.removeEventListener("loadedmetadata", seekAndPlay);
      media.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [start, end]);
  console.log("start, end", start, end);
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.7)] w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2">
            {isVideo ? (
              <Video size={16} className="text-blue-500" />
            ) : (
              <Music size={16} className="text-purple-500" />
            )}
            <span className="text-sm font-semibold text-slate-800 dark:text-white">
              Media Player
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
          >
            <X size={16} className="text-slate-500" />
          </button>
        </div>
        <div className="p-5">
          {isVideo ? (
            <div className="bg-black rounded-2xl overflow-hidden">
              <video
                ref={mediaRef}
                controls
                className="w-full max-h-72 rounded-2xl"
                src={url}
                preload="metadata"
              />
            </div>
          ) : (
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl p-6">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                  <Music size={26} className="text-purple-500" />
                </div>
              </div>
              <audio
                ref={mediaRef}
                controls
                className="w-full rounded-xl"
                src={url}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export const MessageBubble = ({
  msg,
  copiedId,
  setCopiedId,
  handleTryAgain,
  handleEdit,
  darkMode,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(msg.messageText);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [mediaStreamUrl, setMediaStreamUrl] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pages,setPages] = useState([]);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const textareaRef = useRef(null);
  const handleGetMedia = async (link, start, end, pages) => {
    const fileUrl = String(link || "").replace(/[\\"']+$/, "");

    if (!fileUrl) return;

    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(fileUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "*/*",
        },
      });

      if (!res.ok) throw new Error("Failed to load media");

      const data = await res.json();
      const streamUrl = data.url || fileUrl;

      setMediaStreamUrl(streamUrl);
      if (/\.(mp3|mp4|webm|ogg|mov)$/i.test(streamUrl)) {
        setMediaModalOpen(true);
        setStart(Number(start));
        setEnd(Number(end));
      } else if (/\.pdf$/i.test(streamUrl)) {
        setPages(pages);
        setPdfModalOpen(true);
      }
    } catch (e) {
      console.log("Error fetching media:", e);
    }
  };
  useEffect(() => {
    if (isEditing) {
      textareaRef.current?.focus();
      const len = textareaRef.current?.value.length;
      textareaRef.current?.setSelectionRange(len, len);
    }
  }, [isEditing]);

  const handleSaveEdit = () => {
    if (editText.trim() && editText.trim() !== msg.messageText) {
      handleEdit(msg.messageId, editText.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditText(msg.messageText);
    setIsEditing(false);
  };

  const chartSpec = msg.chartSpec || null;
  const chartData = msg.chartData || null;
  const tableData = msg.table || null;
  const sources = msg.senderType === "bot" ? msg.sources || msg.Sources || [] : [];
  const downloads = msg.senderType === "bot" ? msg.downloads || msg.Downloads || [] : [];
  const chartRef = useRef(null);

  const handleCopyTable = (id) => {
    if (!tableData) return;
    const csv = [tableData.columns || tableData.Columns, ...(tableData.rows || tableData.Rows)]
      .map((r) =>
        r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","),
      )
      .join("\r\n");
    navigator.clipboard.writeText(csv).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleDownload = () => {
    if (!tableData) return;
    const csvRows = [
      (tableData.columns || tableData.Columns).join(","),
      ...(tableData.rows || tableData.Rows).map((r) => r.join(","))
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tableData.title || tableData.Title || "table"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleChartDownload = async () => {
    const container = chartRef.current;
    if (!container) return;

    try {
      const dataUrl = await toJpeg(container, {
        quality: 0.95,
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#ffffff",
      });

      const a = document.createElement("a");
      a.download = `${chartSpec?.title || chartSpec?.Title || "chart"}.jpg`;
      a.href = dataUrl;
      a.click();
    } catch (error) {
      console.error("Failed to download chart as JPG:", error);
    }
  };

  return (
    <div
      className={`flex w-full ${msg.senderType === "user" ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`flex flex-col gap-1 group ${msg.senderType === "user" ? "items-end" : "items-start"} ${chartSpec || tableData ? "w-full" : ""}`}
      >
        {/* MESSAGE CONTENT */}
        <div
          className={`text-[15px] leading-relaxed transition-all duration-200 ${
            chartSpec || tableData
              ? "w-full max-w-[100%] p-1"
              : msg.senderType === "user"
                ? "p-3 bg-[#c7d9ef] border border-[#9eb9da] dark:bg-slate-700 dark:border-slate-500 text-slate-900 dark:text-white rounded-[1.25rem] rounded-tr-md max-w-[50vw]"
                : "text-slate-800 dark:text-slate-200 max-w-[75%]"
          }`}
        >
          {!chartSpec &&
            (isEditing ? (
              <textarea
                ref={textareaRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveEdit();
                  }
                  if (e.key === "Escape") handleCancelEdit();
                }}
                rows={Math.max(2, editText.split("\n").length)}
                className="w-full min-w-[280px] bg-transparent border-none outline-none resize-none text-[15px] leading-relaxed"
              />
            ) : (
              <div className="prose prose-sm max-w-none dark:prose-invert break-words">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {typeof msg.messageText === "object"
                    ? JSON.stringify(msg.messageText)
                    : msg.messageText}
                </ReactMarkdown>
              </div>
            ))}
          {chartSpec && (
            <div className="w-full" ref={chartRef}>
              <DynamicChart
                spec={chartSpec}
                data={chartData}
                darkMode={darkMode}
              />
            </div>
          )}
          {tableData && <TableRenderer table={tableData} />}
          {msg.mediaUrl || msg.MediaUrl ? (
            <button
              onClick={() => setMediaModalOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#1d57a5] dark:text-blue-400 hover:underline"
            >
              <Play size={12} /> Open in Media Library
            </button>
          ) : null}
        </div>

        {/* DOWNLOADS */}
        {downloads.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 max-w-[75%]">
            {downloads.map((file, i) => (
              <a
                key={i}
                href={file.Url ?? file.url}
                target="_blank"
                rel="noopener noreferrer"
                download={file.FileName ?? file.fileName}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#1d57a5]/10 dark:bg-blue-900/30 text-[#1d57a5] dark:text-blue-400 border border-[#1d57a5]/20 hover:bg-[#1d57a5]/20 transition-colors truncate max-w-[260px]"
                title={`Download ${file.FileName ?? file.fileName}`}
              >
                <Download size={14} className="flex-shrink-0" />
                <span className="truncate font-medium">{file.FileName ?? file.fileName}</span>
              </a>
            ))}
          </div>
        )}

        {/* SOURCES */}
        {sources.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1 max-w-[75%]">
            {sources.map((src, i) => (
              <button
                onClick={() => handleGetMedia(src.Link ?? src.link, src.start ?? src.Start, src.end ?? src.End, src.pages ?? src.Pages)}
                key={i}
                className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-600 truncate max-w-[260px]"
                title={src.Title ?? src.title ?? "Source Document"}
                rel="noopener noreferrer"
              >
                {src.Title ?? src.title ?? "Source Document"}
              </button>
            ))}
          </div>
        )}

        {/* EDIT ACTIONS */}
        {isEditing && (
          <div className="flex gap-2 mt-1">
            <button
              onClick={handleSaveEdit}
              className="px-4 py-1.5 text-sm bg-[#1d57a5] text-white rounded-full hover:bg-[#174a8c] transition-colors"
            >
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              className="px-4 py-1.5 text-sm bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-full hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {/* MESSAGE ACTIONS */}
        {!chartSpec && !tableData && !isEditing && (
          <div
            className={
              msg.senderType === "user"
                ? ""
                : "w-full border-t border-slate-300/70 dark:border-slate-700/70 mt-3 pt-2 opacity-0 group-hover:opacity-100 transition-opacity"
            }
          >
            <MessageActions
              msgId={msg.messageId}
              msgText={msg.messageText}
              copiedId={copiedId}
              onCopy={(id, text) =>
                copyToClipboard(text, () => {
                  setCopiedId(id);
                  setTimeout(() => setCopiedId(null), 2000);
                })
              }
              onTryAgain={handleTryAgain}
              onEdit={
                msg.senderType === "user" ? () => setIsEditing(true) : undefined
              }
            />
          </div>
        )}
        {chartSpec && !isEditing && (
          <div className="w-full flex justify-start border-t border-slate-300/70 dark:border-slate-700/70 mt-3 pt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <MessageActions
              msgId={msg.messageId}
              msgText={msg.messageText}
              copiedId={copiedId}
              onCopy={(id, text) =>
                copyToClipboard(text, () => {
                  setCopiedId(id);
                  setTimeout(() => setCopiedId(null), 2000);
                })
              }
              onTryAgain={handleTryAgain}
              onDownload={handleChartDownload}
              downloadTitle="Download JPG"
              hideCopy
            />
          </div>
        )}
        {tableData && !isEditing && (
          <div className="w-full flex justify-start border-t border-slate-300/70 dark:border-slate-700/70 mt-3 pt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <MessageActions
              msgId={msg.messageId}
              msgText={msg.messageText}
              copiedId={copiedId}
              onCopy={(id) => handleCopyTable(id)}
              onTryAgain={handleTryAgain}
              onDownload={handleDownload}
              downloadTitle="Download CSV"
            />
          </div>
        )}
      </div>
      {mediaModalOpen && mediaStreamUrl && (
        <MediaPlayerModal
          url={mediaStreamUrl}
          start={start}
          end={end}
          onClose={() => { setMediaModalOpen(false); setMediaStreamUrl(null); }}
        />
      )}
      {pdfModalOpen && mediaStreamUrl && (
        <PdfModal
          url={mediaStreamUrl}
          onClose={() => { setPdfModalOpen(false); setMediaStreamUrl(null); }}
          pages={pages}
        />
      )}
    </div>
  );
};
